<script setup>
import { ref, getCurrentInstance, onMounted, onUnmounted, watch } from 'vue'
import emitter from '../utils/emitter'

const props = defineProps({
    initialCode: {
        type: String,
        default: ''
    }
})

const { proxy } = getCurrentInstance()
const code = ref(props.initialCode)
const useds = ref([])
const error = ref('')
const snackbar = ref(false)

// Watch changes to initialCode prop
watch(() => props.initialCode, (newValue) => {
    if (newValue) {
        code.value = newValue
    }
})

getUsed()

onMounted(() => {
    emitter.on('setRedeemCode', (value) => {
        code.value = value
    })
})

onUnmounted(() => {
    emitter.off('setRedeemCode')
})

async function getUsed() {
    try {
        const res = await proxy.$api({ serviceName: "account", methodName: "codeUsed" })
        useds.value = res
    } catch (err) {
        console.error('Error loading used codes:', err)
    }
}

async function save() {
    error.value = ''
    
    if (!code.value || code.value.trim().length === 0) {
        error.value = 'Please enter a code'
        return
    }

    try {
        const response = await proxy.$http.post("", JSON.stringify({ 
            serviceName: "account", 
            methodName: "redeemcode", 
            parameters: [code.value] 
        }))

        if (response.data.state == "error") {
            error.value = response.data.message
        } else {
            code.value = ""
            snackbar.value = true
            await getUsed()
        }
    } catch (err) {
        error.value = err.response?.data?.error || 'Failed to redeem code'
    }
}
</script>

<template>
<div class="redeemForm">
    <v-snackbar :timeout="2000" color="deep-purple-accent-4" elevation="24" v-model="snackbar">
        Code redeemed successfully!
    </v-snackbar>

    <v-alert class="error" v-if="error" :text="error" type="error"></v-alert>

    <v-form>
        <v-container>
            <!-- Redeem Code Card -->
            <div class="settings-card">
                <div class="settings-card-header">
                    <div class="header-icon">
                        <v-icon size="28">mdi-wallet-giftcard</v-icon>
                    </div>
                    <div class="header-content">
                        <h3 class="settings-title">Redeem Code</h3>
                        <p class="settings-subtitle">Enter your promotional code</p>
                    </div>
                </div>

                <div class="settings-card-body">
                    <!-- Code Input -->
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-ticket-confirmation</v-icon>
                                Promotional Code
                            </div>
                            <div class="setting-description">
                                Type your code here to claim your reward
                            </div>
                            <v-text-field 
                                v-model="code" 
                                placeholder="Enter code" 
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                class="setting-input code-input"
                            ></v-text-field>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Redeem Button -->
            <v-btn 
                class="save-btn" 
                variant="elevated" 
                color="primary"
                size="large"
                @click="save"
                block
            >
                <v-icon class="mr-2">mdi-gift</v-icon>
                Redeem Code
            </v-btn>

            <!-- Used Codes History -->
            <div class="settings-card codes-history">
                <div class="settings-card-header">
                    <div class="header-icon">
                        <v-icon size="28">mdi-history</v-icon>
                    </div>
                    <div class="header-content">
                        <h3 class="settings-title">Redeemed Codes</h3>
                        <p class="settings-subtitle">Your code history</p>
                    </div>
                </div>

                <div class="codes-list">
                    <div v-if="useds.length === 0" class="empty-state">
                        <v-icon size="48" color="primary" class="mb-2">mdi-inbox</v-icon>
                        <div class="empty-text">No codes redeemed yet</div>
                    </div>
                    
                    <div v-else class="code-item" v-for="used in useds" :key="used.code">
                        <div class="code-icon">
                            <v-icon color="primary">mdi-check-circle</v-icon>
                        </div>
                        <div class="code-details">
                            <div class="code-name">{{ used.code }}</div>
                            <div class="code-description">{{ used.name }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </v-container>
    </v-form>
</div>
</template>

<style scoped>
.redeemForm {
    margin: 0;
    padding: 0;
}

.redeemForm .v-icon {
    color: currentColor;
    opacity: 1;
}

.settings-card {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
    margin-bottom: 20px;
}

.settings-card-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: rgba(var(--v-theme-primary), 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: rgba(var(--v-theme-primary), 0.2);
    border-radius: 10px;
}

.header-icon .v-icon {
    color: rgb(var(--v-theme-primary));
}

.header-content {
    flex: 1;
}

.settings-title {
    margin: 0;
    font-size: 1.3em;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
}

.settings-subtitle {
    margin: 4px 0 0 0;
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.6);
}

.settings-card-body {
    padding: 24px;
}

.setting-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.setting-info {
    flex: 1;
}

.setting-info.full-width {
    width: 100%;
}

.setting-label {
    display: flex;
    align-items: center;
    font-size: 1em;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 6px;
}

.setting-label .v-icon {
    color: rgba(255, 255, 255, 0.9);
}

.setting-description {
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 16px;
}

.setting-input {
    margin-top: 0;
}

.code-input {
    text-transform: uppercase;
}

.save-btn {
    margin-bottom: 20px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.save-btn .v-icon {
    opacity: 1 !important;
}

.codes-history {
    margin-bottom: 0;
}

.codes-list {
    max-height: 250px;
    overflow-y: auto;
    overflow-x: hidden;
}

.codes-list::-webkit-scrollbar {
    width: 6px;
}

.codes-list::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
    border-radius: 3px;
}

.codes-list::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-primary), 0.3);
    border-radius: 3px;
}

.codes-list::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--v-theme-primary), 0.5);
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: rgba(255, 255, 255, 0.4);
}

.empty-text {
    font-size: 0.9em;
}

.code-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: rgba(255, 255, 255, 0.02);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    transition: background 0.2s ease;
}

.code-item:hover {
    background: rgba(255, 255, 255, 0.05);
}

.code-item:last-child {
    border-bottom: none;
}

.code-icon {
    flex-shrink: 0;
}

.code-details {
    flex: 1;
}

.code-name {
    font-size: 1em;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 4px;
    font-family: monospace;
}

.code-description {
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.5);
}

.error {
    margin-bottom: 20px;
}
</style>